// @ts-nocheck

// App
import React from 'react';
import { connect } from 'react-redux';
import { yieldCallback } from '@zillow/yield-callback';
import SafeDeferredRender, { RenderMode } from 'app/shared/modules/SafeDeferredRender';

// Actions
import {
  srwmCheckAndFetchNearbyListings,
  srwmFetchAreaDataByResourceId,
  srwmFetchFilterFromUrlAndApi,
  srwmFetchListingsAndOtherData,
} from 'app/shared/pages/SearchResultsPage/serverRouteWillMountFunctions';
import {
  csrCheckAndFetchNearbyListings,
  csrFetchAreaDataByResourceId,
  csrFetchGeoLocation,
  csrFetchListingsAndHistogramData,
  csrFetchListingsAndOtherData,
  csrFetchListingsOnly,
} from 'app/shared/pages/SearchResultsPage/clientSideFetchingFunctions';
import AreaActions from 'app/shared/flux/actions/AreaActions';
import AppActions from 'app/shared/flux/actions/AppActions';

// Components
import AreaInformationWrapper from 'app/shared/modules/MobileSRP/AreaInformationWrapper';
import FilterBar from 'app/shared/modules/MobileSRP/Header/FilterBar';
import Footer from 'app/shared/modules/MobileSRP/Footer';
import List from 'app/shared/modules/MobileSRP/ListingCards/List';
import MapButton from 'app/shared/modules/MobileSRP/Map/MapButton';
import AreaHead from 'app/shared/modules/area/AreaHead';
import NextPageLink from 'app/shared/modules/MobileSRP/NextPageLink';
import NotFoundPage from 'app/shared/pages/NotFoundPage';
import SeoFooter from 'app/shared/modules/MobileSRP/SeoFooter';

// Tracking

// Misc / styles
import * as S from './styles';
import { listingUtils_getResourceIdAndSlug } from 'app/shared/utils/listingUtils';
import queryUtils from 'app/shared/utils/queryUtils';
import routeUtils from 'app/shared/utils/routeUtils';
import loadGeolocation from 'app/shared/wrappers/FetchAreaWrapper/loadGeolocation';
import { emitTrackViewSrpEvent } from 'app/shared/models/Clickstream/PageViewEvents';
import { logError } from '@zg-rentals/log-error';

class SearchResultsPage extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isMapInitialized: false,
    };

    // Create ref that will be used to trigger below the fold API calls
    this.lazyLoadRef = React.createRef();

    // Create Intersection Observer placeholder variable
    this.observer;
    this.scrollBottomObserver;
  }

  /**
   * When component mounts, create the Intersection Observer instance that will be
   * in charge of lazy API fetching below the fold content; content will be loaded
   * when the user scrolls near the end of listings.
   */
  componentDidMount() {
    const {
      dispatch,
      areaResourceId,
      areaPageMaloneLotIdPosition,
      listings,
      isClientSideLoadedPage,
      isInitialSsrPage,
      pathname,
      searchSlug,
      query,
    } = this.props;
    const { resourceId: pathResourceId, slug: pathSlug } = listingUtils_getResourceIdAndSlug(pathname);
    const isNearMeUrl = routeUtils.isNearMeUrl(pathSlug);
    const hasResourceIdChangedFromLastMount = !isNearMeUrl && pathResourceId !== areaResourceId;
    const hasSlugChangedFromLastMount = !isNearMeUrl && pathSlug !== searchSlug;
    const hasUserMovedBackToNearMePage =
      isNearMeUrl && isInitialSsrPage === false && !hasResourceIdChangedFromLastMount && !hasSlugChangedFromLastMount;

    // It's important that we don't needlessly fetch listings if we're going from mWeb SRP => HDP => back to mWeb SRP
    const shouldFetchClientSideListings =
      (isClientSideLoadedPage && isInitialSsrPage === false && listings.length === 0) ||
      hasResourceIdChangedFromLastMount ||
      hasSlugChangedFromLastMount ||
      hasUserMovedBackToNearMePage;

    if (shouldFetchClientSideListings) {
      dispatch(AppActions.setAppStoreBool('fetchListingsByCoordsComplete', false));
      // Is this an area resource id change (i.e. search bar from home page is used)
      this.handleClientSideLoadedSrp(pathname, query);
    }

    // Intersection Observer
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio === 1) {
          // Fetch below the fold content when the ref is fully in view
          this.fetchBelowTheFoldContent();

          // Once below the fold content is called, unobserve the ref to prevent superfluous calls
          this.observer.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: '0px 0px 500px 0px',
        threshold: 1.0,
      },
    );

    this.scrollBottomObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio === 1) {
          // Once tracking is called, unobserve the ref to prevent superfluous calls
          this.scrollBottomObserver.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: '0px 0px 50px 0px',
        threshold: 1.0,
      },
    );

    // Start observering the ref
    if (this.lazyLoadRef.current) {
      this.observer.observe(this.lazyLoadRef.current);
      this.scrollBottomObserver.observe(this.lazyLoadRef.current);
    }

    if (areaPageMaloneLotIdPosition) {
      const scrollPosition = document.getElementById(areaPageMaloneLotIdPosition);
      const HEADER_PADDING = 85;
      if (scrollPosition) {
        document.documentElement.scrollTop = scrollPosition.offsetTop - HEADER_PADDING;
      }
    }

    emitTrackViewSrpEvent();
  }

  /**
   *
   * @param {Object} prevProps  - Previous props provided by React
   * Logic in the componentDidUpdate lifecycle method will watch for change in the app
   * and call functions to fetch for new listings.
   *
   * These changes could be:
   * 1) Area resource id has changed (searching new location)
   * 2) Search slug has changed (searching for different listing types)
   * 3) Query parameters have changed (beds, orderBy, pets, lat, lon, z, etc)
   */
  componentDidUpdate(prevProps) {
    const { dispatch, isHamburgerMenuOpen, pathname, query } = this.props;
    const hasPageChanged = prevProps.pathname === pathname && prevProps.query.page !== query.page;
    const { resourceId: prevResourceId, slug: prevSlug } = listingUtils_getResourceIdAndSlug(prevProps.pathname);
    const { resourceId: currResourceId, slug: currSlug } = listingUtils_getResourceIdAndSlug(pathname);
    const hasAreaResourceIdChanged = prevResourceId !== currResourceId;
    const hasSearchSlugChanged = prevSlug !== currSlug;
    const wasHamMenuJustActive = prevProps.isHamburgerMenuOpen !== isHamburgerMenuOpen;
    const hasUserSwitchedRentalsViaHamburger = wasHamMenuJustActive && hasSearchSlugChanged;

    const hasMapCoordsChanged =
      prevProps.query &&
      (prevProps.query.lat !== query.lat ||
        prevProps.query.lon !== query.lon ||
        prevProps.query.z !== query.z ||
        prevProps.query.border !== query.border);

    // Area resource id has changed
    if (hasAreaResourceIdChanged) {
      this.areaResourceIdChange(pathname, query);

      // Start observing the ref once again
      this.observer.observe(this.lazyLoadRef.current);
      this.scrollBottomObserver.observe(this.lazyLoadRef.current);
      return;
    }

    if (hasUserSwitchedRentalsViaHamburger) {
      dispatch(AppActions.setAppStoreBool('fetchListingsByCoordsComplete', false));
      this.handleClientSideLoadedSrp(pathname, query);
      emitTrackViewSrpEvent();
      return;
    }

    // Search slug has changed
    if (hasSearchSlugChanged) {
      // Listings fetched is handled automatically... need to fire tracking, though
      emitTrackViewSrpEvent();
      return;
    }

    // Query parameters have changed - WORK IN PROGRESS
    if (hasPageChanged) {
      this.queryParamaterChange(query);
      return;
    }

    // Map coordinates have changed
    if (hasMapCoordsChanged) {
      this.mapChange(query);
      return;
    }
  }

  /**
   * Disconnect Intersection Observer when the component unmounts.
   */
  componentWillUnmount() {
    this.observer.disconnect();
    this.scrollBottomObserver.disconnect();
  }

  /**
   *
   * @param {Object} reactContext - SSR context that contains `store`, `trace`, `state`, `ip`
   * Server route will mount is charged with handling all SSR related API fetching
   */
  serverRouteWillMount(reactContext) {
    const { store } = reactContext;
    const state = store.getState();
    const pathname = state.location.current.pathname;
    const { resourceId, slug } = listingUtils_getResourceIdAndSlug(pathname);
    const isNearMeUrl = routeUtils.isNearMeUrl(slug);

    const trace = reactContext.trace;
    const beforeFetchListings = [];

    const fetchAsyncAreaData = (areaResourceId) => {
      // Promises executed in parallel
      beforeFetchListings.push(
        // Fetch filter via search slug
        srwmFetchFilterFromUrlAndApi(reactContext),
        // Fecth area data
        srwmFetchAreaDataByResourceId(reactContext, { areaResourceId }),
      );

      return Promise.all(beforeFetchListings)
        .then((responses) => {
          // Check if there are any errors in the responses i.e. invalid slug or areas
          for (const res of responses) {
            if (res && (res.error || res.changeHttpStatus)) {
              return res;
            }
          }

          return Promise.all(srwmFetchListingsAndOtherData(reactContext));
        })
        .then(() => {
          return srwmCheckAndFetchNearbyListings(reactContext);
        })
        .catch((err) => {
          logError({ error: err, context: 'error in SearchResultsPage#serverRouteWillMount' });
        });
    };

    if (isNearMeUrl) {
      trace.startAsyncSpan({ marker: 'FetchAreaWrapper#loadGeolocationData' });
      return loadGeolocation(reactContext).then((geoData) => {
        // IP address might be out of range for `area/byIp` endpoint
        if (geoData === false) {
          logError({
            error: 'FetchAreaWrapper.serverRouteWillMount.geoPromise',
            errorType: 'SearchResultsPageError',
            context: `No geocode for IP ${reactContext.ip}`,
          });
          // Geo data can't be pulled from IP... use default area
          return fetchAsyncAreaData('new-york-ny');
        }
        const geoDataId = geoData.area && geoData.area.resourceId;

        return fetchAsyncAreaData(geoDataId);
      });
    }

    return fetchAsyncAreaData(resourceId);
  }

  /**
   * SRP page has been client side loaded:
   * - Via search bar menu on homepage
   * - Via "apartments-for-rent" or "homes-for-rent" on ham menu
   *
   * Fetch listings!
   */
  handleClientSideLoadedSrp = async (pathname, query) => {
    const { dispatch } = this.props;
    const { resourceId } = listingUtils_getResourceIdAndSlug(pathname);

    await dispatch(csrFetchAreaDataByResourceId(resourceId));
    await dispatch(csrFetchListingsAndOtherData(pathname, query));
    await dispatch(csrCheckAndFetchNearbyListings());
  };

  /**
   *
   * @param {string} pathname - the url path in the user's browser
   * @param {Object} query - the url query parameters in the user's browser
   *
   * Fetches all area and listings data for client side changes when a user
   * navigates to completely different area - WORK IN PROGRESS...
   */
  areaResourceIdChange = (pathname, query) => {
    const { dispatch } = this.props;
    const isNearMeUrl = routeUtils.isNearMeUrl(pathname);
    const { resourceId } = listingUtils_getResourceIdAndSlug(pathname);
    // Can be used for client side rendering
    // Search slug to filter
    // Area info by resource id

    const fetchAsyncClientSideAreaData = (id) => {
      dispatch(csrFetchAreaDataByResourceId(id))
        .then(async (res) => {
          if (res.success) {
            await dispatch(csrFetchListingsAndOtherData(pathname, query));
            emitTrackViewSrpEvent();
            return { success: true };
          }
        })
        .then(() => {
          dispatch(csrCheckAndFetchNearbyListings());
          return { success: true };
        })
        .catch((err) => {
          logError({ error: err, context: 'error in SearchResultsPage#fetchAreaData' });
        });
    };

    if (isNearMeUrl) {
      dispatch(csrFetchGeoLocation()).then((geoData) => {
        // IP address might be out of range for `area/byIp` endpoint
        if (geoData === false) {
          // Geo data can't be pulled from IP... use default area
          return fetchAsyncClientSideAreaData('new-york-ny');
        }
        const geoDataId = geoData.area && geoData.area.resourceId;

        return fetchAsyncClientSideAreaData(geoDataId);
      });
    } else {
      return fetchAsyncClientSideAreaData(resourceId);
    }
  };

  /**
   *
   * @param {Object} query - the url query parameters in the user's browser
   *
   * Fetches listings data for client side changes when a user triggers a
   * change in query params (page, filters, etc) - WORK IN PROGRESS
   */
  queryParamaterChange = (query) => {
    const { dispatch } = this.props;
    dispatch(csrFetchListingsOnly(query)).then(() => {
      emitTrackViewSrpEvent();
    });
  };

  /**
   *
   * @param {Object} query - the url query parameters in the user's browser
   *
   * Fetches new listing data and price filter histogram data when the user
   * moves the map around
   */
  mapChange = (query) => {
    const { dispatch } = this.props;
    dispatch(csrFetchListingsAndHistogramData(query));
  };

  /**
   * Below the fold content; these API calls are lazily made
   */
  fetchBelowTheFoldContent = () => {
    const { dispatch, areaResourceId, isInitialSsrpPage, searchSlug } = this.props;

    // Lazy load facts if the page is CSR unless it's houses-for-rent
    if (searchSlug !== 'houses-for-rent') {
      // Area facts
      dispatch(AreaActions.loadFacts(areaResourceId));
      dispatch(AreaActions.loadFactsByBeds(areaResourceId));
    }

    // Lazy load articles if the page is CSR; articles will always be fetched on SSR
    if (!isInitialSsrpPage) {
      // Area Articles
      dispatch(AreaActions.loadArticles(areaResourceId));
    }
  };

  /**
   * Triggers initialization of Google maps when
   * 1) All filters button is clicked
   * 2) The Map button is clicked
   */
  onHandleInitializeMapClick = yieldCallback(() => {
    this.setState({
      isMapInitialized: true,
    });
  });

  render() {
    const { listings, isValidAreaPage, isValidSearchSlug } = this.props;
    const shouldRenderNotFoundPage = !isValidAreaPage || !isValidSearchSlug;

    if (shouldRenderNotFoundPage) {
      return <NotFoundPage />;
    }

    return (
      <>
        <AreaHead />
        <S.header>
          <FilterBar />
        </S.header>
        <S.main>
          <MapButton
            isMapInitialized={this.state.isMapInitialized}
            handleInitializeMapClick={this.onHandleInitializeMapClick}
          />
          <List
            items={listings}
            lazyLoadRef={this.lazyLoadRef}
            handleInitializeMapClick={this.onHandleInitializeMapClick}
          />
          <S.SemanticDivider />
          <SafeDeferredRender mode={RenderMode.RENDER_SERVER_SIDE_HYDRATE_ON_IMMEDIATE}>
            <AreaInformationWrapper />
            <NextPageLink />
            <SeoFooter />
          </SafeDeferredRender>
        </S.main>
        <Footer />
      </>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    pathname: ownProps.location.pathname,
    query: queryUtils.parse(ownProps.location.search),
    filter: state.filter,
    areaResourceId: state.area.area.resourceId,
    listings: state.listings.listingGroups.byCoords,
    isValidAreaPage: state.app.isValidAreaPage,
    isValidSearchSlug: state.app.isValidSearchSlug,
    isInitialSsrpPage: state.app.isInitialSsrPage,
    isClientSideLoadedPage: state.app.isClientSideLoadedPage,
    isHamburgerMenuOpen: state.app.hamburger,
    searchSlug: state.filter.search.slug,
    areaPageMaloneLotIdPosition: state.app.areaPageMaloneLotIdPosition,
  };
};

export default connect(mapStateToProps)(SearchResultsPage);
